import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { FiSearch, FiX, FiChevronLeft } from "react-icons/fi";
import { shownTools, fits, toolPage, moduleTool, PRESETS } from "../../lib/tools/registry";
import { extensionOn } from "../../lib/extensions/registry";
import { useCurrentIdeas } from "../assistant/AssistantProvider";
import { useToolPins } from "./pins";
import ToolPanel from "./ToolPanel";
import { countUse } from "./ToolFrame";
import styles from "./tools.module.css";

export const OPEN_TOOLS = "truechops:tools";
const modulesOn = extensionOn("modules");
const kindOf = (idea) => idea?.kind === "recording" ? "audio" : idea?.kind || "nothing";
const describe = (tool) => `Takes ${tool.takes.map((kind) => kind === "nothing" ? "nothing" : kind === "audio" ? "a recording" : `a ${kind}`).join(" or ")} → gives ${tool.gives.join(" or ")}${tool.cost === "studio" ? " · uses studio minutes" : ""}`;

// The tool library (docs/refactor-plan.md, Phase 2): Ctrl+K (⌘K) anywhere, or Tools in the menu.
// It finds tools by name and what they do, the ones that fit what's open first; each can be used
// here, opened on its own page, or pinned to the shelf in Make. The first time, a preset starts the
// shelf.
export default function ToolLibrary() {
  const router = useRouter(), [open, setOpen] = useState(false), [start, setStart] = useState(null);
  useEffect(() => {
    const key = (event) => { if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") { event.preventDefault(); setStart(null); setOpen((value) => !value); } };
    const show = (event) => { setStart(event.detail?.tool || null); setOpen(true); };
    window.addEventListener("keydown", key); window.addEventListener(OPEN_TOOLS, show);
    return () => { window.removeEventListener("keydown", key); window.removeEventListener(OPEN_TOOLS, show); };
  }, []);
  useEffect(() => { setOpen(false); }, [router.asPath]);
  // (What it shows, and your pins, load only once it's open.)
  return open ? <Library start={start} close={() => setOpen(false)} /> : null;
}

function Library({ start, close }) {
  const [query, setQuery] = useState(""), [using, setUsing] = useState(start);
  const ideas = useCurrentIdeas(), idea = ideas[0] || null, { pins, save, toggle } = useToolPins();
  const [community, setCommunity] = useState(null), field = useRef(null), dialog = useRef(null);
  useEffect(() => {
    // Focus moves in: to the search, or to the library itself when it opens on a tool, so Escape works.
    setTimeout(() => (field.current || dialog.current)?.focus(), 0);
    if (modulesOn) fetch("/api/modules?community=1", { credentials: "same-origin" }).then((response) => response.json().catch(() => null).then((data) => response.ok && data ? data : { modules: [] }))
      .then((data) => setCommunity((data.modules || []).slice(0, 12).map(moduleTool))).catch(() => setCommunity([]));
  }, []);
  const tools = useMemo(() => shownTools(), []);
  const words = query.trim().toLowerCase(), matches = (tool) => !words || `${tool.name} ${tool.does}`.toLowerCase().includes(words);
  const kind = kindOf(idea);
  const sections = useMemo(() => {
    const found = tools.filter(matches), fitting = idea ? found.filter((tool) => fits(tool, kind)) : [];
    const sound = found.filter((tool) => !fitting.includes(tool) && tool.gives.includes("audio")), rest = found.filter((tool) => !fitting.includes(tool) && !sound.includes(tool));
    return [[idea ? `Use on ${idea.title}` : "", fitting], ["Make sound", sound], [idea ? "More tools" : "Tools", rest], ["Made by the community", (community || []).filter(matches)]]
      .filter(([title, list]) => title && list.length);
  }, [tools, words, idea?.key, kind, community]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className={styles.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
    <div ref={dialog} tabIndex={-1} className={styles.library} role="dialog" aria-modal="true" aria-label="Tools" onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); using ? setUsing(null) : close(); } }}>
      {using ? <div className={styles.usePanel}>
        <button type="button" className={`${styles.button} ${styles.back}`} onClick={() => setUsing(null)}><FiChevronLeft aria-hidden="true" /> All tools</button>
        <ToolPanel tool={using} />
      </div> : <>
        <div className={styles.search}>
          <FiSearch aria-hidden="true" />
          <input ref={field} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a tool" aria-label="Find a tool" />
          <kbd>esc</kbd>
          <button type="button" className={styles.icon} aria-label="Close the tools" onClick={close}><FiX aria-hidden="true" /></button>
        </div>
        {pins === null && <div className={styles.presets} role="group" aria-label="Start your shelf">
          <span>Start your shelf for</span>
          {PRESETS.map((preset) => <button key={preset.id} type="button" className={styles.button} title={preset.says} onClick={() => save(preset.tools)}>{preset.label}</button>)}
        </div>}
        <div className={styles.libraryBody}>
          {idea && <p className={`${styles.working} ${styles.libraryWorking}`}>Working on <span className={styles.chip}>{idea.title}</span></p>}
          {sections.map(([title, list]) => <section key={title} aria-label={title}>
            <h2 className={styles.section}>{title}</h2>
            <ul className={styles.rows}>{list.map((tool) => {
              const pinned = (pins || []).includes(tool.id);
              return <li key={tool.id} className={styles.row} data-tool={tool.id}>
                <div className={styles.rowText}><strong>{tool.name}</strong><span>{tool.does}</span><small>{tool.by ? `A module by @${tool.by}` : describe(tool)}</small></div>
                <div className={styles.rowActions}>
                  <button type="button" className={styles.button} onClick={() => { countUse(tool.id); setUsing(tool); }} aria-label={`Use ${tool.name}`}>Use</button>
                  <Link className={styles.button} href={toolPage(tool)} aria-label={`Open ${tool.name}`}>Open</Link>
                  {!tool.module && <button type="button" className={`${styles.button} ${styles.pin}`} aria-pressed={pinned} onClick={() => toggle(tool.id)} aria-label={`${pinned ? "Unpin" : "Pin"} ${tool.name}`}>{pinned ? "Pinned" : "Pin"}</button>}
                </div>
              </li>;
            })}</ul>
          </section>)}
          {!sections.length && <p className={`${styles.hint} ${styles.empty}`}>No tool does “{query}”.</p>}
        </div>
        <p className={styles.foot}>Pinned tools sit on your shelf in Make. Every tool also opens on its own page. <Link href="/extensions" data-extensions-entry>Extensions</Link> shows everything this site is made of.</p>
      </>}
    </div>
  </div>;
}
